import {
    useEffect,
    useMemo,
} from 'react';

import {
    addToSelection,
    clearSelection,
    removeFromSelection,
} from '../reducers';

import {selectionSelector} from '../selectors';
import {
    useAppDispatch,
    useAppSelector
} from '../../../hooks/useTypedReactRedux';

import {useTopLevelDomainsQuery} from './useTopLevelDomainsQuery';
import {TopLevelDomain} from '../types';


export const useTopLevelDomainsSelection = () => {
    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));

    // FNP-944: select-all iterates the records React Query fetched.
    const {data} = useTopLevelDomainsQuery();
    const items = data ?? [];


    // SELECTION
    const handleRowSelection = (row: { id: string }) => {
        if (typeof selection[row.id] !== 'undefined') {
            dispatch(removeFromSelection(row.id));
        } else {
            dispatch(addToSelection(row));
        }
    };

    const handleSelectAllRows = () => {
        for (let i = 0; i < items.length; i++) {
            if (typeof selection[items[i].id] !== 'undefined') {
                dispatch(removeFromSelection(items[i].id));
            } else {
                dispatch(addToSelection(items[i]));
            }
        }
    };

    const handleClearSelection = () => {
        dispatch(clearSelection());
    };

    // ⚠️ FNP-1292 — THE ARRAY SHAPE `MantineDataTable` SPEAKS, alongside the
    // map the older consumers read. The table's
    // `selectedRecords`/`onSelectedRecordsChange` pair exchanges whole arrays;
    // the slice stores a map keyed by id. This hook is the one place that
    // converts, so `DeleteRecordButton` and `EditPage` kept their `selection`
    // and `handleClearSelection` untouched.
    const selectedRecords = useMemo(
        () => Object.values(selection) as TopLevelDomain[],
        [selection],
    );

    // The table hands back the WHOLE next selection, not a delta, so this diffs
    // against what is stored and dispatches only the rows that entered or left.
    const setSelectedRecords = (next: TopLevelDomain[]) => {

        const nextIds = new Set(next.map((record) => record.id));

        Object.keys(selection)
              .filter((id) => !nextIds.has(id))
              .forEach((id) => dispatch(removeFromSelection(id)));

        next.filter((record) => typeof selection[record.id] === 'undefined')
            .forEach((record) => dispatch(addToSelection(record)));
    };

    // ⚠️ CLEAR ON UNMOUNT. FNP-1293 proved that Redux-backed selection still
    // survives a route change unless something clears it: an operator ticked a
    // row, navigated away and came back to a stale checkbox against a table
    // that had refetched. Written in here rather than found in a browser again.
    //
    // Safe for the delete path, which clears while still mounted.
    useEffect(() => () => {
        dispatch(clearSelection());
    }, [dispatch]);

    return {
        items,
        selection,
        selectedRecords,
        setSelectedRecords,
        handleRowSelection,
        handleSelectAllRows,
        handleClearSelection,
    };
};
